import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ImagePlus, Star, Trash2, Save, Settings, Compass } from 'lucide-react';
import { SCShell, SCHeading, MultiChips, goldBtn, ghostBtn, RequireAuth } from './sc/SCShared';
import { scGetProfile, scUpdateProfile, scOptions, scUploadPhoto, scDeletePhoto, scSetPrimaryPhoto, scFileUrl } from '../api';

const Field = ({ label, children }) => (
  <div><label className="font-jost text-xs uppercase tracking-widest text-[#f5d786] mb-2 block">{label}</label>{children}</div>
);
const inputCls = 'w-full bg-black/30 border border-[#8b5cf6]/30 rounded-xl px-4 py-3 text-[#e9d8ff] focus:outline-none focus:border-[#d946ef]';

const Inner = () => {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [opts, setOpts] = useState(null);
  const [p, setP] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => scGetProfile().then((data) => {
    if (!data || data.age_verification_status !== 'verified') { navigate('/sacred-connections/onboarding'); return; }
    setP(data);
    setForm({
      display_name: data.display_name || '', bio: data.bio || '', gender: data.gender || '', pronouns: data.pronouns || '',
      location_area: data.location_area || '', approx_city: data.approx_city || '', distance_preference: data.distance_preference || '',
      relationship_intention: data.relationship_intention || '', family_goals: data.family_goals || '', spiritual_identity: data.spiritual_identity || '',
      spiritual_interests: data.spiritual_interests || [], lifestyle: data.lifestyle || [], connection_style: data.connection_style || [],
      hobbies: (data.hobbies || []).join(', '),
    });
  }).catch(() => navigate('/sacred-connections/onboarding'));

  useEffect(() => { scOptions().then(setOpts); load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, hobbies: form.hobbies.split(',').map((s) => s.trim()).filter(Boolean) };
      const updated = await scUpdateProfile(payload);
      setP(updated); toast.success('Profile saved.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save.'); } finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { await scUploadPhoto(f); await load(); toast.success('Photo added.'); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed.'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const delPhoto = async (id) => { try { await scDeletePhoto(id); await load(); } catch { toast.error('Could not remove photo.'); } };
  const makePrimary = async (id) => { try { await scSetPrimaryPhoto(id); await load(); } catch { toast.error('Could not set primary.'); } };

  if (!p || !opts) return <SCShell><p className="font-cormorant text-2xl text-[#e9d8ff]/60 animate-pulse">Loading…</p></SCShell>;

  return (
    <SCShell>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <SCHeading eyebrow="My Profile" title="Your Sacred Connections profile" />
        <div className="flex gap-3">
          <Link to="/sacred-connections/discover" className={ghostBtn} data-testid="sc-profile-discover"><Compass className="h-4 w-4" /> Discover</Link>
          <Link to="/sacred-connections/settings" className={ghostBtn} data-testid="sc-profile-settings"><Settings className="h-4 w-4" /> Settings</Link>
        </div>
      </div>

      {/* Photos */}
      <div className="rounded-2xl p-6 border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md mb-6">
        <p className="font-jost text-xs uppercase tracking-widest text-[#f5d786] mb-4">Photos</p>
        <div className="flex flex-wrap gap-4" data-testid="sc-photos">
          {(p.photos_meta || []).map((ph) => (
            <div key={ph.id} className="relative group">
              <img src={scFileUrl(ph.url)} alt="profile" className={`h-28 w-28 object-cover rounded-xl border ${ph.is_primary ? 'border-[#f5d786]' : 'border-[#8b5cf6]/30'}`} />
              <div className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                {!ph.is_primary && <button onClick={() => makePrimary(ph.id)} title="Set primary" className="text-[#f5d786]"><Star className="h-5 w-5" /></button>}
                <button onClick={() => delPhoto(ph.id)} title="Remove" className="text-[#ff6b9d]"><Trash2 className="h-5 w-5" /></button>
              </div>
              {ph.is_primary && <span className="absolute top-1 left-1 text-[9px] bg-[#f5d786] text-black px-1.5 py-0.5 rounded-full">Primary</span>}
            </div>
          ))}
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sc-photo-add" className="h-28 w-28 rounded-xl border border-dashed border-[#8b5cf6]/40 flex flex-col items-center justify-center text-[#e9d8ff]/60 hover:border-[#d946ef]">
            <ImagePlus className="h-6 w-6 mb-1" /><span className="text-xs">{uploading ? 'Uploading…' : 'Add'}</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
        </div>
      </div>

      {/* Fields */}
      <div className="rounded-2xl p-6 border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md grid md:grid-cols-2 gap-5">
        <Field label="Display name"><input className={inputCls} value={form.display_name} onChange={(e) => set('display_name', e.target.value)} data-testid="sc-f-name" /></Field>
        <Field label="Pronouns"><input className={inputCls} value={form.pronouns} onChange={(e) => set('pronouns', e.target.value)} placeholder="she/her, he/him, they/them…" /></Field>
        <Field label="Gender"><input className={inputCls} value={form.gender} onChange={(e) => set('gender', e.target.value)} /></Field>
        <Field label="General area (city / region)"><input className={inputCls} value={form.location_area} onChange={(e) => set('location_area', e.target.value)} placeholder="Austin, TX" data-testid="sc-f-area" /></Field>
        <Field label="Approximate city (optional)"><input className={inputCls} value={form.approx_city} onChange={(e) => set('approx_city', e.target.value)} placeholder="For distance context only" /></Field>
        <Field label="Distance preference"><input className={inputCls} value={form.distance_preference} onChange={(e) => set('distance_preference', e.target.value)} placeholder="e.g. Within 25 miles" /></Field>
        <div className="md:col-span-2"><Field label="Short bio"><textarea rows={3} className={inputCls} value={form.bio} onChange={(e) => set('bio', e.target.value)} data-testid="sc-f-bio" /></Field></div>
        <Field label="Looking for">
          <select className={inputCls} value={form.relationship_intention} onChange={(e) => set('relationship_intention', e.target.value)} data-testid="sc-f-intention">
            <option value="">Select…</option>
            {opts.relationship_intentions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Family goals">
          <select className={inputCls} value={form.family_goals} onChange={(e) => set('family_goals', e.target.value)}>
            <option value="">Prefer not to say</option>
            {opts.family_goals.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2"><Field label="Spiritual interests"><MultiChips options={opts.spiritual_interests} value={form.spiritual_interests} onChange={(v) => set('spiritual_interests', v)} testid="sc-f-spiritual" /></Field></div>
        <div className="md:col-span-2"><Field label="Lifestyle"><MultiChips options={opts.lifestyle} value={form.lifestyle} onChange={(v) => set('lifestyle', v)} testid="sc-f-lifestyle" /></Field></div>
        <div className="md:col-span-2"><Field label="Connection style"><MultiChips options={opts.connection_styles} value={form.connection_style} onChange={(v) => set('connection_style', v)} /></Field></div>
        <div className="md:col-span-2"><Field label="Hobbies (comma separated)"><input className={inputCls} value={form.hobbies} onChange={(e) => set('hobbies', e.target.value)} placeholder="Painting, trail running…" /></Field></div>
        <div className="md:col-span-2"><button onClick={save} disabled={saving} className={goldBtn} data-testid="sc-profile-save"><Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save profile'}</button></div>
      </div>
    </SCShell>
  );
};

const SCProfile = () => <RequireAuth><Inner /></RequireAuth>;
export default SCProfile;
